import {
  useState } from "react";
import { ASPECT_PRESETS,
  normalizeAspect,
  aspectLabel,
  type AssetSpec,
  arTargetKey,
  arVideoKey,
  arOverlayKey,
  arPosterKey,
  arSpineKey,
} from "@greg-chick/shared";
import { generateNarration, uploadAsset, type ManifestEntry } from "../lib/contentApi";
import { SlotUploader } from "./SlotUploader";
import { ActionButton } from "./ActionButton";
import { ArOverlayEditor } from "./ArOverlayEditor";
import { ArMagicEditor } from "./ArMagicEditor";
import { compileTarget, imageFromUrl, fileToImage } from "../lib/arCompile";
import { QrPrint } from "./QrPrint";

/**
 * 전자책 **페이지 하나**를 다루는 조각들 — 읽어주기(TTS)·비율·AR.
 *
 * BookEditor 가 872줄이라 "페이지 편집"과 "책 목록·저장"이 한 파일에 섞여 있었다
 * (docs/build/20 B5). 이 셋은 페이지 하나만 보면 되는 잎(leaf) 컴포넌트라 먼저 떼어냈다.
 *
 * ⚠️ 나레이션은 **운영자가 듣고 올리는** 흐름이다 — 만들자마자 게시되지 않는다.
 *    AI 는 초안, 사람이 승인(docs/build/00 S4).
 */

// AI 나레이션 목소리(엣지 ALLOWED_VOICES 와 일치)
const NARRATION_VOICES: { id: string; label: string }[] = [
  { id: "nova", label: "따뜻한 (기본)" },
  { id: "shimmer", label: "밝은" },
  { id: "fable", label: "이야기꾼" },
  { id: "coral", label: "부드러운" },
  { id: "onyx", label: "낮은 목소리" },
];

/** 음성 나레이션 — 파일 올리기(기존) + 자막을 읽어주는 AI 목소리 생성(TTS). 한국어·영어. */
export function NarrationSection({
  audioSpec,
  koKey,
  enKey,
  koLegacy,
  enLegacy,
  koText,
  enText,
  assets,
  setEntry,
}: {
  audioSpec: AssetSpec;
  koKey: string;
  enKey: string;
  koLegacy: string;
  enLegacy: string;
  koText: string;
  enText: string;
  assets: Record<string, ManifestEntry>;
  setEntry: (key: string, entry: ManifestEntry | null) => void;
}) {
  const [voice, setVoice] = useState("nova");
  // 정식 키 우선, 없으면 레거시(비네임스페이스) 폴백. 편집 시 정식 키로 이관.
  const entryOf = (nsKey: string, legacyKey: string) => assets[nsKey] ?? assets[legacyKey];
  const setMedia = (nsKey: string, legacyKey: string) => (_k: string, e: ManifestEntry | null) => {
    setEntry(nsKey, e);
    if (assets[legacyKey]) setEntry(legacyKey, null);
  };

  async function genAudio(key: string, legacyKey: string, text: string, lang: string) {
    if (!text.trim()) throw new Error(`먼저 ${lang} 자막을 입력하세요. 그 글을 목소리로 읽어줘요.`);
    const blob = await generateNarration(text, voice);
    const file = new File([blob], `${key}.mp3`, { type: "audio/mpeg" });
    const url = await uploadAsset(key, file);
    setEntry(key, { url, alt: `AI 나레이션(${lang})` });
    if (assets[legacyKey]) setEntry(legacyKey, null);
  }

  return (
    <div className="bk-audio">
      <div className="bk-narr__voice">
        <span>🎙️ AI 목소리</span>
        <select value={voice} onChange={(e) => setVoice(e.target.value)}>
          {NARRATION_VOICES.map((v) => (
            <option key={v.id} value={v.id}>{v.label}</option>
          ))}
        </select>
        <span className="bk-narr__hint">자막을 읽어 음성을 만들어요(OpenAI 열쇠 필요).</span>
      </div>

      <div className="bk-narr__lang">
        <SlotUploader slotKey={koKey} label="🔊 음성 나레이션(한국어)" spec={audioSpec} entry={entryOf(koKey, koLegacy)} onChange={setMedia(koKey, koLegacy)} kind="audio" />
        <ActionButton onAction={() => genAudio(koKey, koLegacy, koText, "한국어")} doneLabel="만들었어요 — [🚀 웹에 올리기]로 반영" className="btn btn--sm">
          🤖 자막으로 AI 목소리 만들기
        </ActionButton>
      </div>

      <div className="bk-narr__lang">
        <SlotUploader slotKey={enKey} label="🔊 음성 나레이션(English·선택)" spec={audioSpec} entry={entryOf(enKey, enLegacy)} onChange={setMedia(enKey, enLegacy)} kind="audio" />
        <ActionButton onAction={() => genAudio(enKey, enLegacy, enText, "영어")} doneLabel="만들었어요 — [🚀 웹에 올리기]로 반영" className="btn btn--sm">
          🤖 English 자막으로 AI 목소리
        </ActionButton>
      </div>
    </div>
  );
}

/** 페이지 비율(가로:세로) 선택 — 프리셋 버튼 + 직접 입력(커스텀). 미니 미리보기로 모양 확인. */
export function PageAspect({ aspect, onSet, onApplyAll }: { aspect: string; onSet: (v: string) => void; onApplyAll: () => void }) {
  const isPreset = ASPECT_PRESETS.some((p) => p.value === aspect);
  const [cw, setCw] = useState("");
  const [ch, setCh] = useState("");
  const [w = "1", h = "1"] = aspect.split("/");

  function applyCustom() {
    const v = normalizeAspect(`${cw}/${ch}`);
    if (v) onSet(v);
  }

  return (
    <div className="pg-aspect">
      <div className="pg-aspect__head">
        <b>페이지 모양(비율)</b>
        <span className="pg-aspect__cur">현재: {aspectLabel(aspect)}</span>
      </div>
      <div className="pg-aspect__row">
        {/* 미니 미리보기 — 고른 비율 모양 */}
        <span className="pg-aspect__mini" style={{ aspectRatio: aspect }} aria-hidden="true" />
        <div className="pg-aspect__presets">
          {ASPECT_PRESETS.map((p) => (
            <button key={p.id} type="button" className={aspect === p.value ? "on" : ""} onClick={() => onSet(p.value)}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="pg-aspect__custom">
        <span>직접:</span>
        <input type="number" min={1} placeholder={w} value={cw} onChange={(e) => setCw(e.target.value)} aria-label="가로" />
        <span>:</span>
        <input type="number" min={1} placeholder={h} value={ch} onChange={(e) => setCh(e.target.value)} aria-label="세로" />
        <button type="button" className="btn btn--sm" disabled={!cw || !ch} onClick={applyCustom}>적용</button>
        {!isPreset ? <span className="pg-aspect__badge">직접 {w}:{h}</span> : null}
      </div>
      <button type="button" className="pg-aspect__all" onClick={onApplyAll}>이 비율을 모든 페이지에 적용</button>
    </div>
  );
}

/** 페이지 통합 AR(FR-AR-01) — 이 페이지 그림을 인식 타깃으로 만들고 오버레이를 지정한다.
 *  타깃은 페이지 그림에서 바로 컴파일(따로 사진 안 올림), 다른 그림으로도 가능. */
export function PageArSection({
  bookId,
  pageId,
  pageLabel,
  pageImageUrl,
  pageAspect,
  pageVideo,
  assets,
  setEntry,
}: {
  bookId: string;
  pageId: string;
  pageLabel: string;
  pageImageUrl?: string;
  /** 아이 화면이 쓰는 쪽 비율(CSS `aspect-ratio`). 자리 좌표가 이 상자 기준이라 **같아야** 한다. */
  pageAspect?: string;
  pageVideo?: string;
  assets: Record<string, ManifestEntry>;
  setEntry: (key: string, entry: ManifestEntry | null) => void;
}) {
  const idKey = `${bookId}.${pageId}`;
  const targetKey = arTargetKey(idKey);
  const target = assets[targetKey];
  /**
   * 📷 **카메라가 켜져 있나는 「자리가 있나」가 아니라 「카메라 주소가 있나」다** (2026-09-13 운영자 신고).
   * [카메라 끄기]는 자리를 남기고 주소만 비운다(마법을 살리려고). 그런데 배지·단추·QR 이 전부 `target` 만 보고 있어서
   * 눌러도 **화면이 하나도 안 바뀌었다** — 「✓ 카메라 준비 완료」가 그대로였고 운영자는 됐는지 알 길이 없었다.
   */
  const camOn = !!target?.url;
  const [open, setOpen] = useState(!!target);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [altFile, setAltFile] = useState<File | null>(null);
  const [status, setStatus] = useState<{ kind: "info" | "ok" | "err"; msg: string } | null>(null);

  async function makeTarget(fromAlt: boolean) {
    if (busy) return;
    setStatus(null);
    // 원인을 눈에 보이게 — 조용한 no-op 금지(과거: 그림 없으면 버튼 disabled 라 반응 없어 보였음)
    if (fromAlt && !altFile) {
      setStatus({ kind: "err", msg: "다른 그림 파일을 먼저 고르세요." });
      return;
    }
    if (!fromAlt && !pageImageUrl) {
      setStatus({ kind: "err", msg: "먼저 위에서 ‘페이지 그림’을 올려 주세요. 카메라는 그 그림을 보고 알아봐요." });
      return;
    }
    setBusy(true);
    setProgress(0);
    let revoke: string | null = null;
    try {
      const img = fromAlt ? await fileToImage(altFile!) : await imageFromUrl(pageImageUrl!);
      if (fromAlt) revoke = img.src;
      setStatus({ kind: "info", msg: "카메라가 그림을 외우는 중이에요… (1~2분 걸려요, 창을 닫지 마세요)" });
      const url = await compileTarget(targetKey, img, idKey, (p) => setProgress(p));
      /**
       * 🔴 **얹는다. 갈아 끼우지 않는다.** (2026-09-10 운영 사고)
       *
       * 이 한 줄이 `{ url, alt }` 로 **통째로 바꾸고** 있었다. 그 자리에는 그 쪽의 **마법 정의**
       * (`interact`)와 「영상으로 보기」 주소(`fallbackYoutube`)가 같이 산다 — 카메라를 다시
       * 외우게 한 것뿐인데 **7쪽 마법이 통째로 사라졌다.** 아무 에러도 안 났고, 관리자 화면은
       * 「층 0」만 보여 줬다(만든 적 없는 것처럼).
       *
       * 같은 자리에 쓰는 다른 세 곳은 전부 `...target` 을 편다. 여기만 빠져 있었다.
       */
      setEntry(targetKey, { ...(target ?? {}), url, alt: `${pageLabel} AR 타깃` });
      setAltFile(null);
      setStatus({ kind: "ok", msg: "✓ 다 외웠어요! 위의 [💾 저장]을 눌러야 진짜로 켜져요." });
    } catch (e) {
      const raw = (e as Error)?.message || "";
      setStatus({
        kind: "err",
        msg: /compiler_missing|import|chunk|Failed to fetch dynamically/i.test(raw)
          ? "AR 도구를 불러오지 못했어요. 새로고침하고 다시 해보세요."
          : raw || "그림 외우기에 실패했어요. 그림이 너무 크거나 흐릿하면 다른 그림으로 해보세요.",
      });
    } finally {
      setBusy(false);
      setProgress(null);
      if (revoke) URL.revokeObjectURL(revoke);
    }
  }

  const enabled = open || !!target;
  /** 이 페이지에 올려 둔 AR 재료 수 — 타깃이 없으면 **잠들어 있다**는 걸 말해 주려고 센다. */
  const arAssetCount = [
    arVideoKey(idKey),
    arOverlayKey(idKey),
    arPosterKey(idKey),
    arSpineKey(idKey, "json"),
  ].filter((k) => assets[k]?.url).length;
  const mainLabel = busy
    ? progress !== null
      ? `그림 외우는 중 ${progress}%…`
      : "준비 중…"
    : camOn
      ? "페이지 그림으로 다시 외우게 하기"
      : "이 그림을 카메라가 알아보게 하기";

  return (
    <div className="bk-ar">
      <label className="bk-check bk-ar__toggle">
        <input type="checkbox" checked={enabled} onChange={(e) => setOpen(e.target.checked)} />
        <b>🪄 이 페이지에 AR 켜기</b> — 아이가 이 페이지를 비추면 그림이 살아나요(움직이는 그림·영상·🧊 3D 모델은 켜면 아래에 나와요)
      </label>

      {enabled ? (
        <div className="bk-ar__body">
          <div className="bk-ar__target">
            <span className={`slot__badge${camOn ? " slot__badge--real" : ""}`}>
              {camOn ? "✓ 카메라 준비 완료" : target?.interact ? "카메라 꺼짐 (✨ 마법 보기는 돼요)" : "카메라 준비 안 됨"}
            </span>
            <button type="button" className="btn btn--primary btn--sm" disabled={busy} onClick={() => void makeTarget(false)}>
              {mainLabel}
            </button>
            {camOn && !busy ? (
              /**
               * 📷 **카메라만 끈다 — 마법·영상 주소는 남긴다.** 예전엔 자리를 통째로 지워(`null`) 그 쪽의 마법 정의까지
               * 사라졌다(다시 외우게 하기와 같은 사고 모양). 카메라가 못 알아보는 쪽(25쪽 별하늘, 2026-09-13)은 카메라 파일만
               * 내리고 「✨ 마법 보기」는 살려야 한다 — 주소를 비우면 AR 목록·[AR로 보기]가 안 뜨고 책 뷰어 마법은 그대로다.
               * 남는 것이 하나도 없으면 그때만 자리를 지운다.
               */
              <button
                type="button"
                className="slot__remove"
                onClick={() => {
                  const { url: _u, alt: _a, ...rest } = target;
                  setEntry(targetKey, Object.keys(rest).length > 0 ? { ...rest, url: "" } : null);
                  // 🔴 눌렀으면 눌렸다고 말한다 — 저장 전까지는 아이 화면이 그대로라는 것까지
                  setStatus({ kind: "ok", msg: "✓ 카메라를 껐어요. 위의 [💾 저장]을 눌러야 아이 화면에서도 꺼져요. 「✨ 마법 보기」는 그대로 돼요." });
                }}
              >
                카메라 끄기 (마법·영상 주소는 남아요)
              </button>
            ) : null}
          </div>

          {status ? <p className={`bk-ar__status bk-ar__status--${status.kind}`}>{status.msg}</p> : null}
          {!pageImageUrl && !status ? <p className="zone__tip">먼저 위에서 <b>페이지 그림</b>을 올려 주세요. 카메라는 그 그림을 보고 알아봐요.</p> : null}

          {/* 🔴 **아이 화면에 무엇이 보이는지**를 여기서 말해 준다.
              AR 을 켜고 영상·그림까지 올렸는데 **타깃(카메라가 외운 그림)이 없으면 전자책에
              [AR로 보기] 버튼이 아예 안 뜬다.** 화면·에러 어디에도 그 말이 없어서, 운영자는
              다 해 놓고 「왜 안 보이지」만 겪는다(2026-08-23 신고).
              올려 둔 것이 있는데 잠들어 있으면 그것까지 적는다 — 지운 게 아니라 잠든 것이다. */}
          {!camOn && target?.interact ? (
            <p className="bk-ar__blocked">
              📷 <b>카메라가 꺼져 있어요</b> — 전자책 [AR로 보기]·AR 목록·QR 에 이 쪽이 안 떠요. <b>「✨ 마법 보기」는 그대로 돼요.</b>{" "}
              다시 켜려면 위 <b>[{mainLabel}]</b>.
            </p>
          ) : null}
          {!target ? (
            <p className="bk-ar__blocked">
              ⚠️ 지금은 <b>전자책에 [AR로 보기] 버튼이 안 떠요.</b> 위 <b>[{mainLabel}]</b> 를 눌러
              카메라가 이 그림을 외우게 해야 아이 화면에 나타나요.
              {arAssetCount > 0 ? (
                <>
                  {" "}올려 두신 <b>영상·그림 {arAssetCount}개</b>는 그때 함께 살아나요(지워지지 않았어요).
                </>
              ) : null}
            </p>
          ) : null}

          {camOn ? (
            <label className="bk-ar__fallback">
              📺 인식 안 될 때 “영상으로 보기”(유튜브·선택)
              <div className="ar-studio__row">
                <input
                  value={String(target.fallbackYoutube ?? "")}
                  placeholder="https://youtu.be/… (없으면 폴백은 안내문만)"
                  onChange={(e) => setEntry(targetKey, { ...target, fallbackYoutube: e.target.value || undefined })}
                />
                {pageVideo && pageVideo !== target.fallbackYoutube ? (
                  <button type="button" className="btn btn--sm" onClick={() => setEntry(targetKey, { ...target, fallbackYoutube: pageVideo })}>
                    페이지 영상 쓰기
                  </button>
                ) : null}
              </div>
            </label>
          ) : null}

          {/* QR 은 카메라가 켜진 쪽에만 — 꺼진 쪽의 QR 을 인쇄하면 아이가 카메라를 켜고 헛되이 비춘다 */}
          {camOn ? <ArPageQr idKey={idKey} pageLabel={pageLabel} /> : null}

          <details className="bk-ar__alt">
            <summary>다른 그림을 알아보게 하고 싶어요(선택)</summary>
            <div className="ar-studio__row">
              <label className="slot__file">
                {altFile ? altFile.name : "다른 그림 고르기"}
                <input type="file" accept="image/*" hidden onChange={(e) => setAltFile(e.target.files?.[0] ?? null)} />
              </label>
              <button type="button" className="btn btn--sm" disabled={busy} onClick={() => void makeTarget(true)}>이 그림 외우게 하기</button>
            </div>
          </details>

          <ArOverlayEditor idKey={idKey} title={pageLabel} assets={assets} setEntry={setEntry} />

          {/* 🪄 누르면 일어나는 마법(층·누르는 자리·손잡이) — docs/build/35 */}
          <ArMagicEditor
            idKey={idKey}
            title={pageLabel}
            pageImageUrl={pageImageUrl}
            pageAspect={pageAspect}
            assets={assets}
            setEntry={setEntry}
          />
        </div>
      ) : null}
    </div>
  );
}

/** 페이지별 AR QR — 인쇄물(책·엽서)에 붙이면 스캔 시 그 페이지 AR 로 바로 진입(/ar?scene=<책>.<페이지>). */
function ArPageQr({ idKey, pageLabel }: { idKey: string; pageLabel: string }) {
  return <QrPrint path={`/ar?scene=${encodeURIComponent(idKey)}`} title={`📷 ${pageLabel} AR`} />;
}
